import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Spinner } from '../components'
import { FormField } from '../components/FormField'
import { useMoistureEquilibriumContext } from '../context/moistureEquilibrium'
import { useViabilityConstants } from '../hooks/useViabilityConstants'
import { getTaxonNameDisplay, type ViabilityConstants } from '../util'

export type ViabilityConstantsFormData = {
  [K in keyof ViabilityConstants]: string
}

export const transformData = <T extends ViabilityConstantsFormData>(data: T) =>
  Object.fromEntries(
    Object.entries(data).map(([key, val]) => [key, parseFloat(val)]),
  ) as unknown as Record<keyof T, number>

export const ViabilityConstantsForm = ({
  setValues,
}: {
  setValues: (results: ViabilityConstantsFormData) => void
}) => {
  const vabilityConstants = useViabilityConstants()
  const [reference, setReference] = useState<string>()
  const { setCalculatingMoistureEquilibrium, moistureEquilibrium } =
    useMoistureEquilibriumContext()

  const { register, setValue, watch } = useForm<ViabilityConstantsFormData>()

  useEffect(() => {
    setValue('moistureContent', moistureEquilibrium?.toFixed(1) ?? '')
  }, [moistureEquilibrium, setValue])

  useEffect(() => {
    const subscription = watch((result) => {
      setValues(
        Object.fromEntries(
          Object.entries(result).map(([key, value]) => [key, value ?? '']),
        ) as ViabilityConstantsFormData,
      )
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [setValues, watch])

  const setViabilityConstantsPreset = useCallback(
    (presetId: number) => {
      if (!presetId) {
        setValue('Ke', '')
        setValue('Ch', '')
        setValue('Cq', '')
        setValue('Cw', '')
        return
      }

      const vc = vabilityConstants?.find(
        (viability) => viability.id === presetId,
      )
      if (!vc) return
      if (vc.reference) setReference(vc.reference.description)

      const { ke, ch, cq, cw } = vc
      ke && setValue('Ke', ke.toString())
      ch && setValue('Ch', ch.toString())
      cq && setValue('Cq', cq.toString())
      cw && setValue('Cw', cw.toString())
    },
    [vabilityConstants, setValue],
  )

  return (
    <div>
      <div className="form-group mb-2">
        <label htmlFor="input" className="text-1xl mb-2 text-gray-800">
          Select a preset
        </label>
        {vabilityConstants && (
          <select
            onChange={(ev) => {
              setViabilityConstantsPreset(parseInt(ev.target.value))
            }}
            className="block w-full rounded-md border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-serblue-light focus:ring-serblue-light"
          >
            <option value={undefined}>Select an option</option>
            {vabilityConstants.map((vc) => (
              <>
                {vc.species && vc.reference && (
                  <option key={vc.id} value={vc.id}>
                    {getTaxonNameDisplay(vc.species)} {vc.reference.citation}
                  </option>
                )}
              </>
            ))}
          </select>
        )}
        {!vabilityConstants && <Spinner />}
        {reference && (
          <div className="mt-1 rounded-md border border-gray-300 bg-gray-200 p-2 text-xs">
            Reference: {reference}
          </div>
        )}
      </div>
      <FormField
        label={
          <span>
            K<sub>E</sub>
          </span>
        }
        registerProps={register('Ke', { required: true })}
      />
      <FormField
        label={
          <span>
            C<sub>W</sub>
          </span>
        }
        registerProps={register('Cw', { required: true })}
      />
      <FormField
        label={
          <span>
            C<sub>H</sub>
          </span>
        }
        registerProps={register('Ch', { required: true })}
      />
      <FormField
        label={
          <span>
            C<sub>Q</sub>
          </span>
        }
        registerProps={register('Cq', { required: true })}
      />
      <FormField
        label="Equilibrium Moisture Content %"
        registerProps={register('moistureContent', { required: true })}
        button={
          <button
            className="rounded border-2 border-serblue-light bg-white px-0.5 text-xs"
            onClick={() => {
              setCalculatingMoistureEquilibrium(true)
            }}
          >
            Calculate
          </button>
        }
      />

      <FormField
        label="Storage Temp °C"
        registerProps={register('temp', { required: true })}
      />
    </div>
  )
}
